import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { useTranslation } from "react-i18next";

export interface StreakBadgeProps {
  currentStreak: number;
  longestStreak: number;
  size?: "small" | "medium" | "large";
  showLongest?: boolean;
}

const StreakBadge: React.FC<StreakBadgeProps> = ({
  currentStreak,
  longestStreak,
  size = "medium",
  showLongest = false,
}) => {
  const { t } = useTranslation();
  // Determine color based on streak length
  const getStreakColor = (streak: number): string => {
    if (streak === 0) return "#9e9e9e"; // Grey
    if (streak < 7) return "#ff9800"; // Orange
    if (streak < 30) return "#f44336"; // Red
    return "#ffd700"; // Gold
  };

  // Determine icon size
  const getIconSize = (): "small" | "medium" | "large" => {
    switch (size) {
      case "small":
        return "small";
      case "large":
        return "large";
      default:
        return "medium";
    }
  };

  // Determine font size
  const getFontSize = (): string => {
    switch (size) {
      case "small":
        return "0.75rem";
      case "large":
        return "1.25rem";
      default:
        return "1rem";
    }
  };

  const streakColor = getStreakColor(currentStreak);
  const iconSize = getIconSize();
  const fontSize = getFontSize();

  // Build tooltip with translations and pluralization
  const tooltipTitle =
    longestStreak > 0
      ? currentStreak === 1 && longestStreak === 1
        ? t("streaks.badge.tooltip", {
            current: currentStreak,
            longest: longestStreak,
          })
        : t("streaks.badge.tooltip_plural", {
            current: currentStreak,
            longest: longestStreak,
          })
      : currentStreak === 1
      ? t("streaks.badge.tooltipSimple", { current: currentStreak })
      : t("streaks.badge.tooltipSimple_plural", { current: currentStreak });

  return (
    <Tooltip title={tooltipTitle} arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          backgroundColor:
            currentStreak > 0 ? `${streakColor}20` : "transparent",
          border: `1px solid ${streakColor}`,
          cursor: "default",
        }}
      >
        <LocalFireDepartmentIcon
          fontSize={iconSize}
          sx={{ color: streakColor }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize,
            color: streakColor,
            lineHeight: 1,
          }}
        >
          {currentStreak}
        </Typography>
        {showLongest && longestStreak > 0 && (
          <Typography
            variant="caption"
            sx={{
              fontSize: fontSize,
              color: "text.secondary",
              ml: 0.5,
            }}
          >
            (best: {longestStreak})
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default StreakBadge;
